import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { ExpandedContent, Separator } from "../../Compartilhado/styles";
import { IItemHistorico } from "../../../../types";
import { DadosDemandaEditada } from "./types";
import { mapearDadosPropostaEditada, mapearPartesEditadas, mapearAnexosEditados } from "./utils";
import BlocoAlteracoes from "./Blocos/BlocoAlteracoes/index";
import BlocoPartes from "./Blocos/BlocoPartes/index";
import BlocoAnexos from "./Blocos/BlocoAnexos/index";
import LogsVazio from "../../Compartilhado/LogsVazio";
import { useLogHistorico } from "../../Hooks/useLogHistorico";
import api from "../../../../../../../services/api";

interface ExpandedContentProps {
    item: IItemHistorico;
    isExpanded?: boolean;
    onNavigateToFile?: (fileId: number) => void;
}

export default function ExpandedContentDemandaEditada({ item, isExpanded, onNavigateToFile }: ExpandedContentProps) {
    const { dados: dadosIniciais, loading } = useLogHistorico<DadosDemandaEditada>({
        itemId: item.id,
        isExpanded,
        mapearDados: (logs) => ({
            campos: mapearDadosPropostaEditada(logs),
            partes: mapearPartesEditadas(logs),
            anexos: mapearAnexosEditados(logs)
        }),
        dadosVazios: {
            campos: [],
            partes: [],
            anexos: []
        }
    });

    const [dados, setDados] = useState<DadosDemandaEditada | null>(dadosIniciais);
    const params = useParams<{ id: string }>();
    const idProposta = params.id ? Number(params.id) : undefined;

    useEffect(() => {
        if (!dadosIniciais) {
            setDados(dadosIniciais);
            return;
        }

        if (!idProposta) {
            setDados(dadosIniciais);
            return;
        }

        const verificarEstadoAnexos = async () => {
            try {
                const anexosResponse = await api.get(`proposta/${idProposta}/anexos`);
                const anexosAtuais = anexosResponse.data?.result || [];
                
                const anexosAtualizados = dadosIniciais.anexos.map((anexoAlteracao) => {
                    if (anexoAlteracao.tipo === 'removido' || !anexoAlteracao.anexo.id) {
                        return anexoAlteracao;
                    }
                    
                    const anexoAtual = anexosAtuais.find(
                        (a: any) => a.id_proposta_arquivo === anexoAlteracao.anexo.id
                    );
                    
                    if (anexoAtual && anexoAtual.excluido === true) {
                        return {
                            ...anexoAlteracao,
                            anexo: {
                                ...anexoAlteracao.anexo,
                                excluido: true
                            }
                        };
                    }
                    
                    return anexoAlteracao;
                });
                
                setDados({
                    ...dadosIniciais,
                    anexos: anexosAtualizados
                });
            } catch (error) {
                console.error('Erro ao verificar estado dos anexos:', error);
                setDados(dadosIniciais);
            }
        };

        verificarEstadoAnexos();
    }, [dadosIniciais, idProposta]);

    if (loading) {
        return (
            <ExpandedContent>
                <div style={{ padding: '20px', textAlign: 'center', color: 'var(--c64748b)' }}>
                    Carregando dados...
                </div>
            </ExpandedContent>
        );
    }

    if (!dados) {
        return (
            <ExpandedContent>
                <Separator />
                <LogsVazio />
            </ExpandedContent>
        );
    }

    const temDadosValidos = dados.campos.length > 0 || dados.partes.length > 0 || dados.anexos.length > 0;

    if (!temDadosValidos) {
        return (
            <ExpandedContent>
                <Separator />
                <LogsVazio />
            </ExpandedContent>
        );
    }

    return (
        <ExpandedContent>
            <Separator />
            <BlocoAlteracoes campos={dados.campos} />
            <BlocoPartes partes={dados.partes} />
            <BlocoAnexos anexos={dados.anexos} onNavigateToFile={onNavigateToFile} />
        </ExpandedContent>
    );
}

